import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import type { PrimitiveAtom } from 'jotai';

import { useAbortableRequest } from './use-abortable-request';

interface UseAbortableMutationOptions<TData, TError, TVariables, TContext>
  extends Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'> {
  mutationFn: (variables: TVariables, signal: AbortSignal) => Promise<TData>;
  pendingAtom: PrimitiveAtom<boolean>;
  requestKey: string;
}

export const useAbortableMutation = <TData, TError = Error, TVariables = void, TContext = unknown>({
  mutationFn,
  pendingAtom,
  requestKey,
  ...options
}: UseAbortableMutationOptions<TData, TError, TVariables, TContext>) => {
  const { abort, execute, isPending } = useAbortableRequest({
    pendingAtom,
    request: mutationFn,
    requestKey,
  });

  const mutation = useMutation<TData, TError, TVariables, TContext>({
    ...options,
    mutationFn: execute,
  });

  return {
    ...mutation,
    abortMutation: abort,
    isPending,
  };
};
